import { useState } from 'react'
import type { ExtraMenuItemAdmin } from 'api/generated'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import {
  createExtraMenuItem,
  updateExtraMenuItem,
} from 'store/extraMenuItems/extraMenuItemsSlice'
import { useAppDispatch, useAppSelector } from 'store/store'

const LABEL_MAX = 40

// Come nel backend (ExtraMenuItem::URL_FORMAT): path dell'app o URL http(s).
const isValidUrl = (url: string) =>
  /^\/(?!\/)\S*$/.test(url) || /^https?:\/\/\S+$/.test(url)

type FormState = {
  label: string
  url: string
  position: string
  active: boolean
}

const emptyForm = (position: number): FormState => ({
  label: '',
  url: '',
  position: String(position),
  active: true,
})

/** Crea (item null) o modifica una voce aggiuntiva del menu hamburger. */
const ExtraMenuItemDialog = ({
  item,
  nextPosition,
  onOpenChange,
}: {
  item: ExtraMenuItemAdmin | null
  nextPosition: number
  onOpenChange: (open: boolean) => void
}) => {
  const dispatch = useAppDispatch()
  const saving = useAppSelector((s) => s.extraMenuItems.saving)
  // Montato a ogni apertura (key nella pagina): il form parte dalla voce o vuoto.
  const [form, setForm] = useState<FormState>(() =>
    item
      ? {
          label: item.label,
          url: item.url,
          position: String(item.position),
          active: item.active,
        }
      : emptyForm(nextPosition)
  )

  const label = form.label.trim()
  const url = form.url.trim()
  const urlError = url && !isValidUrl(url)
  const canSave =
    !!label && label.length <= LABEL_MAX && !!url && !urlError && !saving
  const external = /^https?:\/\//.test(url)

  const save = async () => {
    const input = {
      label,
      url,
      position: Number(form.position) || 0,
      active: form.active,
    }
    const result = item
      ? await dispatch(updateExtraMenuItem({ id: item.id, input }))
      : await dispatch(createExtraMenuItem(input))
    if (result.meta.requestStatus === 'fulfilled') onOpenChange(false)
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{item ? 'Modifica voce' : 'Nuova voce'}</DialogTitle>
          <DialogDescription>
            La voce compare nel menu hamburger di sito e app.
          </DialogDescription>
        </DialogHeader>
        <form
          className='space-y-4'
          onSubmit={(event) => {
            event.preventDefault()
            if (canSave) save()
          }}
        >
          <div className='space-y-2'>
            <Label htmlFor='extra-menu-label'>Nome</Label>
            <Input
              id='extra-menu-label'
              value={form.label}
              maxLength={LABEL_MAX}
              placeholder='Es. Promo estate'
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='extra-menu-url'>Link</Label>
            <Input
              id='extra-menu-url'
              value={form.url}
              placeholder='/club-membership oppure https://…'
              aria-invalid={!!urlError}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
            <p
              className={
                urlError
                  ? 'text-sm text-destructive'
                  : 'text-sm text-muted-foreground'
              }
            >
              {urlError
                ? 'Usa un path che inizia con / o un URL http(s).'
                : url
                  ? external
                    ? 'Link esterno: si apre nel browser sul web e nel browser interno nelle app.'
                    : 'Link interno: si apre dentro sito e app.'
                  : "Un path dell'app (/club-membership) si apre dentro l'app, un URL http(s) fuori."}
            </p>
          </div>
          <div className='flex items-end gap-6'>
            <div className='w-28 space-y-2'>
              <Label htmlFor='extra-menu-position'>Posizione</Label>
              <Input
                id='extra-menu-position'
                type='number'
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
              />
            </div>
            <div className='flex items-center gap-2 pb-2'>
              <Switch
                id='extra-menu-active'
                checked={form.active}
                onCheckedChange={(active) => setForm({ ...form, active })}
              />
              <Label htmlFor='extra-menu-active'>Attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              Annulla
            </Button>
            <Button type='submit' disabled={!canSave}>
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ExtraMenuItemDialog
